/**
 * Monitor runs admin page.
 *
 * Lists all import monitors for the org and, for each, the recent run
 * history with status, timing, error reason, and the persisted state
 * blob. Closes the visibility gap where admins previously had no way to
 * see why a tick failed without going through the database.
 *
 * Surface conventions copied from ScheduledJobsPage (the prompt-agent
 * sibling): same header style, rows that expand inline. Each monitor
 * shows the same health as its collection's header. The backend
 * endpoints (`/admin/import-jobs/{id}/runs` + `/runs/{run_id}`) mirror
 * the prompt-agent shape so a future shared "monitor runs" UI could
 * fold both together.
 */

import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FiActivity, FiChevronDown, FiChevronRight } from 'react-icons/fi';

import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Heading } from '../../../ui/heading';
import IconWrapper from '../../../ui/IconWrapper';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../ui/table';
import { Timestamp } from '../../../ui/timestamp';
import { useAuth } from '../../../../contexts/AuthContext';
import { isAdminUser } from '../../../../utils/api';
import {
  useDeleteImportJob,
  useImportJobRunAdmin,
  useImportJobRunsAdmin,
  useImportJobsAdmin,
  usePauseImportJob,
  useResumeImportJob,
} from '../../../../hooks/useImportJobsAdmin';
import type { ImportJob, ImportJobRunSummary, RunStatus } from '../../../../types/importJobs';
import { monitorHealthCopy, type UnhealthyStatus } from '../../../../services/types/collection';
import { formatRelativeTime } from '../../../v4/records/CollectionMonitorSummary';
import { useNotice } from '../../shared/NoticeProvider';
import ConfirmDialog from '../scheduled-jobs/ConfirmDialog';
import ImportJobActions from './ImportJobActions';

const SKILL_LABELS: { [k: string]: string } = {
  'intercom-reporting': 'Intercom',
  'dataeq-analyse': 'DataEQ',
};

const CADENCE_LABELS: { [k: string]: string } = {
  every_30m: 'Every 30 min',
  every_1h: 'Every hour',
  every_3h: 'Every 3 hours',
  every_6h: 'Every 6 hours',
  every_12h: 'Every 12 hours',
  every_24h: 'Daily',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

const STATUS_TONE: { [k: string]: string } = {
  succeeded: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
  failed: 'bg-rose-100 text-rose-800 hover:bg-rose-100',
  running: 'bg-sky-100 text-sky-800 hover:bg-sky-100',
  pending: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  cancelled: 'bg-slate-100 text-slate-700 hover:bg-slate-100',
  skipped: 'border-border bg-muted text-muted-foreground hover:bg-muted',
};

// The collection header's tones, as badges.
const HEALTH_TONE: Record<UnhealthyStatus, string> = {
  warning: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50',
  critical:
    'border-[var(--go-berry)]/25 bg-[color:rgba(157,11,77,0.06)] text-[var(--go-berry)] hover:bg-[color:rgba(157,11,77,0.06)]',
};

const INCLUDE_CLOSED_NOTE =
  'Closed conversations are skipped — this monitor never imports a conversation already closed when it first sees it.';

// An Intercom monitor skips closed conversations unless `include_closed` is true: the
// runtime reads a missing key as false.
const skipsClosedConversations = (job: ImportJob): boolean =>
  job.source_skill_name === 'intercom-reporting' && job.source_config?.['include_closed'] !== true;

const RUNS_PAGE_SIZE = 10;

const formatSource = (job: ImportJob): string => {
  const skill = SKILL_LABELS[job.source_skill_name] || job.source_skill_name;
  const cadence = (job.schedule_config?.['cadence'] as string | undefined) || '';
  const cadenceLabel = CADENCE_LABELS[cadence] || cadence;
  return cadenceLabel ? `${skill} · ${cadenceLabel}` : skill;
};

const StatusPill: React.FC<{ status: RunStatus }> = ({ status }) => (
  <Badge className={`${STATUS_TONE[status] || STATUS_TONE.cancelled} capitalize`}>
    {status}
  </Badge>
);

/**
 * Per-summary-field labels. Keeps the run detail readable without the
 * admin having to mentally translate snake_case keys.
 */
const SUMMARY_LABELS: { [k: string]: string } = {
  fetched_count: 'Fetched',
  created_count: 'Created',
  skipped_duplicate_count: 'Skipped (duplicate)',
  failed_count: 'Failed',
  pages_fetched: 'Pages fetched',
  page_cap_hit: 'Hit the record cap',
  reached_boundary: 'Reached the boundary',
  cursor_advanced: 'Cursor advanced',
  fetched_new: 'Fetched new records',
  updated_count: 'Updated',
  dead_lettered_count: 'Quarantined this run',
  backlog_pending: 'Awaiting classification',
  dead_letters_outstanding: 'Quarantined, outstanding',
  detail_not_found: 'Deleted at source',
  source_record_type: 'Record type',
  cursor_before: 'Cursor before',
  cursor_after: 'Cursor after',
};
// The order the run detail lists them in; an unlabelled key follows, as its raw name.
const SUMMARY_ORDER = Object.keys(SUMMARY_LABELS);

const formatSummaryValue = (v: unknown): string => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

/**
 * Renders the expanded run details: structured import summary (fetched /
 * created / skipped / failed / cursors) + error string + free-form state.
 * Loads `/runs/{id}` lazily — the list endpoint returns summary only;
 * detail carries `import_summary`, `state`, `output`, and `error`.
 *
 * `import_summary` is the primary admin-readable surface — populated by
 * `app/worker/tasks/import_jobs.py` after every successful tick with the
 * counts and cursors. `state` is a free-form per-source extension that
 * shouldn't carry the standard counts; we render it only if a source
 * happens to write to it. A skipped run shows only its skip: its error and
 * state each just repeat the reason.
 */
const RunDetailPanel: React.FC<{ jobId: string; runId: string }> = ({ jobId, runId }) => {
  const { data, isLoading, isError } = useImportJobRunAdmin(jobId, runId);
  if (isLoading) return <div className="text-xs text-muted-foreground">Loading run detail…</div>;
  if (isError || !data) return <div className="text-xs text-red-600">Could not load run detail.</div>;

  // Merge the structured `import_summary` payload with the top-level
  // cursor_before / cursor_after columns — the worker writes those onto
  // the row separately from the summary (see ScheduledJobRunResponse) so
  // they have to be merged here for the cursor rows to ever appear.
  const summary: Record<string, unknown> = {
    ...(data.import_summary || {}),
    ...(data.cursor_before ? { cursor_before: data.cursor_before } : {}),
    ...(data.cursor_after ? { cursor_after: data.cursor_after } : {}),
  };
  const summaryEntries = Object.entries(summary);
  const orderedSummary = SUMMARY_ORDER
    .filter((k) => k in summary)
    .map((k) => [k, summary[k]] as [string, unknown])
    .concat(summaryEntries.filter(([k]) => !SUMMARY_ORDER.includes(k)));
  const isSkip = data.status === 'skipped';

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
      {data.error && !isSkip && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Error</div>
          <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-rose-50 px-2 py-1 text-xs text-rose-900">
            {data.error}
          </pre>
        </div>
      )}

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Run summary</div>
        {isSkip ? (
          <p className="mt-1 rounded bg-card px-3 py-2 text-xs text-muted-foreground">
            {data.error ? `Skipped — ${data.error}.` : 'Skipped.'} No run started, so nothing was fetched.
          </p>
        ) : orderedSummary.length > 0 ? (
          <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 rounded bg-card px-3 py-2 text-xs">
            {orderedSummary.map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="text-muted-foreground">{SUMMARY_LABELS[k] || k}</dt>
                <dd className="font-medium text-foreground break-all">{formatSummaryValue(v)}</dd>
              </React.Fragment>
            ))}
          </dl>
        ) : (
          <p className="mt-1 rounded bg-card px-3 py-2 text-xs text-muted-foreground">
            No summary recorded — the run may not have reached the source fetch step.
          </p>
        )}
      </div>

      {!isSkip && data.state && Object.keys(data.state).length > 0 && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Extra state</div>
          <pre className="mt-1 overflow-x-auto rounded bg-card px-2 py-1 text-[11px] leading-5 text-foreground">
            {JSON.stringify(data.state, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

/**
 * One job's runs, inline under its row, newest first and ten to a page: a
 * monitor on a paused collection writes a skip row every slot.
 */
const RunsPanel: React.FC<{ jobId: string }> = ({ jobId }) => {
  const [offset, setOffset] = useState(0);
  const { data, isLoading, isError } = useImportJobRunsAdmin(jobId, RUNS_PAGE_SIZE, offset);
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  if (isLoading) return <div className="px-4 py-3 text-xs text-muted-foreground">Loading runs…</div>;
  if (isError || !data) return <div className="px-4 py-3 text-xs text-red-600">Could not load runs.</div>;
  if (data.items.length === 0)
    return (
      <div className="px-4 py-3 text-xs text-muted-foreground">
        No runs yet. The next scheduled tick will create one.
      </div>
    );

  // From the response rather than local state, so the range and the buttons
  // describe the rows on screen while the next page loads.
  const goTo = (next: number) => {
    setOpenRunId(null);
    setOffset(next);
  };
  const hasNewer = data.offset > 0;
  const hasOlder = data.offset + RUNS_PAGE_SIZE < data.total;

  return (
    <div className="bg-muted/10 px-4 py-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead className="w-32 text-[11px] uppercase tracking-wide">Status</TableHead>
            <TableHead className="w-40 text-[11px] uppercase tracking-wide">Started</TableHead>
            <TableHead className="w-40 text-[11px] uppercase tracking-wide">Finished</TableHead>
            <TableHead className="text-[11px] uppercase tracking-wide">Error</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.items.map((run: ImportJobRunSummary) => {
            const isOpen = openRunId === run.id;
            return (
              <React.Fragment key={run.id}>
                <TableRow
                  className="cursor-pointer hover:bg-muted/30"
                  onClick={() => setOpenRunId(isOpen ? null : run.id)}
                >
                  <TableCell className="py-2">
                    <IconWrapper
                      icon={isOpen ? FiChevronDown : FiChevronRight}
                      className="size-4 text-muted-foreground"
                    />
                  </TableCell>
                  <TableCell className="py-2">
                    <StatusPill status={run.status} />
                  </TableCell>
                  <TableCell className="py-2 text-xs text-muted-foreground">
                    <Timestamp value={run.started_at} format="relative" />
                  </TableCell>
                  <TableCell className="py-2 text-xs text-muted-foreground">
                    <Timestamp value={run.finished_at} format="relative" />
                  </TableCell>
                  <TableCell className="py-2 text-xs">
                    {run.error ? (
                      <span
                        className={`line-clamp-1 max-w-[28rem] ${
                          run.status === 'skipped' ? 'text-muted-foreground' : 'text-rose-700'
                        }`}
                      >
                        {run.error}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
                {isOpen && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={5} className="bg-card p-3">
                      <RunDetailPanel jobId={jobId} runId={run.id} />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      {data.total > RUNS_PAGE_SIZE && (
        <div className="mt-2 flex items-center justify-end gap-2">
          <span className="text-xs text-muted-foreground">
            {`${data.offset + 1}–${data.offset + data.items.length} of ${data.total}`}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasNewer}
            onClick={() => goTo(Math.max(0, data.offset - RUNS_PAGE_SIZE))}
          >
            Newer
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasOlder}
            onClick={() => goTo(data.offset + RUNS_PAGE_SIZE)}
          >
            Older
          </Button>
        </div>
      )}
    </div>
  );
};

const ImportJobsAdminPage: React.FC = () => {
  const { user } = useAuth();
  const { data, isLoading, isError } = useImportJobsAdmin();
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ImportJob | null>(null);

  const notice = useNotice();
  const pauseMutation = usePauseImportJob();
  const resumeMutation = useResumeImportJob();
  const deleteMutation = useDeleteImportJob();

  const handlePause = async (job: ImportJob) => {
    try {
      await pauseMutation.mutateAsync(job.id);
      notice.show({ type: 'success', message: `Paused "${job.name}"`, autoDismissMs: 3000 });
    } catch (error) {
      notice.show({
        type: 'error',
        message: 'Failed to pause monitor',
        details: error instanceof Error ? [error.message] : undefined,
        autoDismissMs: 6000,
      });
    }
  };

  const handleResume = async (job: ImportJob) => {
    try {
      await resumeMutation.mutateAsync(job.id);
      notice.show({ type: 'success', message: `Resumed "${job.name}"`, autoDismissMs: 3000 });
    } catch (error) {
      notice.show({
        type: 'error',
        message: 'Failed to resume monitor',
        details: error instanceof Error ? [error.message] : undefined,
        autoDismissMs: 6000,
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      notice.show({ type: 'success', message: `Deleted "${deleteTarget.name}"`, autoDismissMs: 4000 });
      if (openJobId === deleteTarget.id) setOpenJobId(null);
      setDeleteTarget(null);
    } catch (error) {
      notice.show({
        type: 'error',
        message: 'Failed to delete monitor',
        details: error instanceof Error ? [error.message] : undefined,
        autoDismissMs: 6000,
      });
    }
  };

  if (!isAdminUser(user)) {
    return <Navigate to="/v3/dashboard" replace />;
  }

  return (
    <div className="space-y-6 px-6 py-8">
      <div className="flex items-center justify-between">
        <div>
          <Heading level={1} className="flex items-center gap-2">
            <IconWrapper icon={FiActivity} className="size-6 text-primary" />
            Monitor runs
          </Heading>
          <p className="mt-1 text-sm text-muted-foreground">
            Recent import-job runs across all monitors in your org. Click a row to see run history,
            errors, and the persisted state from each tick.
          </p>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Monitor</TableHead>
              <TableHead className="w-56">Source</TableHead>
              <TableHead className="w-32">Status</TableHead>
              <TableHead className="w-44">Last run</TableHead>
              <TableHead className="w-44">Next run</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                  Loading monitors…
                </TableCell>
              </TableRow>
            )}
            {isError && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-sm text-rose-700">
                  Could not load monitors.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && (data?.items.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                  No monitors yet. Create one from the Monitor Conduct page → New monitor.
                </TableCell>
              </TableRow>
            )}
            {(data?.items ?? []).map((job) => {
              const isOpen = openJobId === job.id;
              const health = monitorHealthCopy(job.health_status, job.health_reason);
              return (
                <React.Fragment key={job.id}>
                  <TableRow
                    className="cursor-pointer hover:bg-muted/20"
                    onClick={() => setOpenJobId(isOpen ? null : job.id)}
                  >
                    <TableCell className="py-3">
                      <IconWrapper
                        icon={isOpen ? FiChevronDown : FiChevronRight}
                        className="size-4 text-muted-foreground"
                      />
                    </TableCell>
                    <TableCell className="py-3">
                      <div className="text-sm font-medium text-foreground">{job.name}</div>
                      {health && (
                        <Badge className={`mt-1 ${HEALTH_TONE[health.status]}`} title={health.hint ?? undefined}>
                          {job.health_since
                            ? `${health.label} · since ${formatRelativeTime(job.health_since)}`
                            : health.label}
                        </Badge>
                      )}
                      {skipsClosedConversations(job) && (
                        <p className="mt-1 text-xs text-amber-700">{INCLUDE_CLOSED_NOTE}</p>
                      )}
                    </TableCell>
                    <TableCell className="py-3 text-sm text-muted-foreground">
                      {formatSource(job)}
                    </TableCell>
                    <TableCell className="py-3">
                      <Badge
                        className={
                          job.status === 'active'
                            ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 capitalize'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-100 capitalize'
                        }
                      >
                        {job.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 text-xs text-muted-foreground">
                      <Timestamp value={job.last_run_at} format="relative" fallback="Never" />
                    </TableCell>
                    <TableCell className="py-3 text-xs text-muted-foreground">
                      <Timestamp value={job.next_run_at} format="future" />
                    </TableCell>
                    <TableCell
                      className="py-3 text-right"
                      // Don't let a click in the actions cell toggle the row.
                      onClick={(event) => event.stopPropagation()}
                    >
                      <ImportJobActions
                        job={job}
                        onPause={handlePause}
                        onResume={handleResume}
                        onDelete={(j) => setDeleteTarget(j)}
                        disabled={pauseMutation.isPending || resumeMutation.isPending}
                      />
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={7} className="p-0">
                        <RunsPanel jobId={job.id} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete monitor?"
        description={
          deleteTarget
            ? `"${deleteTarget.name}" and its run history will be permanently removed. This does not delete records already imported into the collection.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        isSubmitting={deleteMutation.isPending}
        onConfirm={handleDelete}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null);
        }}
      />
    </div>
  );
};

export default ImportJobsAdminPage;
